import { useContext, useState, createContext } from 'react';
import { API } from 'aws-amplify';
import { useAuth } from './Auth';
import { useApiError } from './ApiErrorProvider';
import { error } from '../constants/bindings';
import { getEasternTime } from '../helpers/dateHelpers';
import { SdoAdminExceptionRevokeResponse, PilotAutocomplete, SdoAdminRequestAPI, SdoDates, SdoPositionAPI, SdoPosition, SdoPositionAvailability, SdoLimitsAPI } from '../models/SdoInterface';

const sdoAdminRequestDefault: Array<SdoAdminRequestAPI> = [];
const pilotAutocompleteDefault: Array<PilotAutocomplete> = [];
const pilotRequestDefault: SdoAdminRequestAPI = {} as any;
const exceptionRevokeResponseDefault: SdoAdminExceptionRevokeResponse = {} as any; 
const sdoDatesDefault: Array<SdoDates> = [];
const sdoPositionsDefault: Array<SdoPositionAPI> = [];
const positionAvailabilityDataDefault: SdoLimitsAPI = {lastEmployeeId: '', requestList: []};
const manualUpdateDefault =  {} as any; 
const currentMonthLimitChangedDefault = {value: false, timestamp: Date.now()}; 
const sdoSettingsDefault = {} as any; 

const SdoAdminEligibilityCriteriaContext = createContext({
  loading: false,
  pilotRequestHistoryLoading: false,
  exceptionRevokeLoading: false,
  limitsLoading: false,
  requestsData: sdoAdminRequestDefault,
  pilotAutocomplete: pilotAutocompleteDefault,
  pilotRequestsData: pilotRequestDefault,
  exceptionRevokeResponse: exceptionRevokeResponseDefault,
  sdoDates: sdoDatesDefault,
  sdoPositions: sdoPositionsDefault,
  positionAvailabilityData: positionAvailabilityDataDefault,
  manualUpdate: manualUpdateDefault,
  currentMonthLimitChanged: currentMonthLimitChangedDefault,
  sdoSettings: sdoSettingsDefault,
  getManageRequestsData: (employeeId: string, month: number, position, year: number, allDates?) => {},
  getPilotAutocomplete: () => {},
  getSdoDates: () => {},
  getSdoPositions: () => {},
  grantCancelException: (employeeId: string, year: number, createdOn?: string) => {},
  getPilotRequestHistory: (employeeId: string, month, year: number) => {},
  revokeRequest: (employeeId: string, month: number, year: number, reason: string, createdOn: string, transactionId: number) => {},
  viewPosition: (position: SdoPosition,sdoMonthYear: any) => {},
  saveAvailabilities: (month: number, position: SdoPosition, year: number, limits: Array<SdoPositionAvailability>, reset?: boolean) => {},
  checkCurrentMonthManualUpdateChanged: (month: number, position: SdoPosition, year: number) => {},
  checkCurrentMonthAvailabilityChanged: async(month: number, position: SdoPosition, year: number, currentMonthAvailability: number) => {},
  getSdoInfoTabData: async() => {}
});

export const SdoAdminEligibilityCriteriaProvider = (props) => {
  const { user } = useAuth();
  const { setErrorCode } = useApiError();
  const [requestsData, setRequestsData] = useState(sdoAdminRequestDefault);
  const [pilotAutocomplete, setPilotAutocomplete] = useState(pilotAutocompleteDefault);
  const [loading, setLoading] = useState(false);
  const [pilotRequestHistoryLoading, setPilotRequestHistoryLoading] = useState(false);
  const [pilotRequestsData, setPilotRequestsData] = useState(pilotRequestDefault);
  const [exceptionRevokeResponse, setExceptionRevokeResponse] = useState(exceptionRevokeResponseDefault);
  const [exceptionRevokeLoading, setExceptionRevokeLoading] = useState(false);
  const [sdoDates, setSdoDates] = useState(sdoDatesDefault); 
  const [sdoPositions, setSdoPositions] = useState(sdoPositionsDefault); 
  const [limitsLoading, setLimitsLoading] = useState(false);
  const [positionAvailabilityData, setPositionAvailabilityData] = useState(positionAvailabilityDataDefault);
  const [manualUpdate, setManualUpdate] = useState(manualUpdateDefault);
  const [currentMonthLimitChanged, setCurrentMonthLimitChanged] = useState({value: false, timestamp: Date.now()});
  const [sdoSettings, setSdoSettings] = useState(sdoSettingsDefault);

  const getManageRequestsData = async(employeeId: string, month: number, position: string | SdoPosition, year: number, allDates?) => {
    if(!user) return;
    try {
      setLoading(true);
      let requestBody = {
        sdoMonth: month,
        sdoYear: year
      };
      if(employeeId) requestBody['employeeId'] = employeeId; 
      else {
        if(allDates) requestBody['sdoRange'] = -1; 
        if(typeof(position) === 'object') requestBody['position'] = position; 
      }
      const requestObject = {
        body: requestBody
      };
      const manageRequestsData = await API.post('crewlife', `/sdo/eligibility/retrieve`, requestObject);
      setRequestsData(manageRequestsData.requestList);
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  };

  const getPilotAutocomplete = async() => {
    if(!user) return;
    try {
      setLoading(true); 
      const pilots = await API.get('crewlife', `/sdo/pilots`, {});
      setPilotAutocomplete(pilots.results); 
      setLoading(false); 
    } catch (e: any) {
      setLoading(false); 
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  }

  const getPilotRequestHistory = async(employeeId: string, month: number, year: number) => {
    if(!user) return; 
    try {
      setPilotRequestHistoryLoading(true);
      const requestObject = {
        body: {
          employeeId: employeeId,
          sdoMonth: month,
          sdoYear: year
        }
      };
      const pilotRequests = await API.post('crewlife', `/sdo/eligibility/retrieve`, requestObject);
      setPilotRequestsData(pilotRequests.requestList[0]);
      setPilotRequestHistoryLoading(false);
    } catch (e: any) {
      setPilotRequestHistoryLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status); 
      else setErrorCode(error.DEFAULT); 
    }
  }

  const getSdoDates = async() => {
    if(!user) return;
    try {
      setLoading(true); 
      const sdoDates = await API.get('crewlife', `/sdo/admin/sdodropdown`, {});
      setSdoDates(sdoDates.sdoMonths); 
      setLoading(false); 
    } catch (e: any) {
      setLoading(false); 
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  }

  const getSdoPositions = async() => {
    if(!user) return;
    try {
      setLoading(true); 
      const sdoPositions = await API.get('crewlife', `/sdo/positions`, {});
      setSdoPositions(sdoPositions.results); 
      setLoading(false); 
    } catch (e: any) {
      setLoading(false); 
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  }

  const grantCancelException = async(employeeId: string, year: number, createdOn?: string) => {
    if(!user) return; 
    try {
      setExceptionRevokeLoading(true);
      let requestBody = {
        employeeId: employeeId,
        year: year
      }
      if(createdOn) requestBody['createdOn'] = createdOn; 
      const requestObject = {
        body: requestBody
      };
      const exceptionGranted = await API.post('crewlife', `/sdo/exemption`,requestObject);
      setExceptionRevokeResponse({
        timestamp: Date.now(),
        response: exceptionGranted
      })
      setExceptionRevokeLoading(false);
    } catch (e: any) {
      setExceptionRevokeLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT); 
    }
  }

  const revokeRequest = async(employeeId: string, month: number, year: number, reason: string, createdOn: string, transactionId: number) => {
    if(!user) return; 
    try {
      setExceptionRevokeLoading(true);
      const requestObject = {
        body: {
          employeeId: employeeId,
          month: month,
          year: year,
          status: "REVOKED",
          reason: reason,
          createdOn: createdOn,
          transactionId
        }
      };
      const requestRevoked = await API.post('crewlife', `/sdo/revoke`, requestObject);
      setExceptionRevokeResponse({
        timestamp: Date.now(),
        response: requestRevoked 
      })
      setExceptionRevokeLoading(false);
    } catch (e: any) {
      setExceptionRevokeLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status); 
      else setErrorCode(error.DEFAULT);
    }
  }

  const saveAvailabilities = async(month: number, position: SdoPosition, year: number, limits: Array<SdoPositionAvailability>, reset?: boolean) => {
    if(!user) return; 
    try {
      setLimitsLoading(true);
      const requestObject = {
        body: {
          sdoMonth: month,
          sdoYear: year,
          rank: position.rank,
          base: position.base,
          equipment: position.equipment,
          limits: limits
        }
      };
      if(reset) {
        requestObject.body['reset'] = true; 
        requestObject.body['manualOverride'] = true; 
      }else{
        requestObject.body['reset'] = false; 
        requestObject.body['manualOverride'] = false; 
      }
      await API.post('crewlife', `/sdo/limits`, requestObject);
      viewPosition(position,{sdoMonth:month,sdoYear:year});
    } catch (e: any) {
      setLimitsLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  }

  const viewPosition = async(position: SdoPosition, sdoMonthYear) => {
    if(!user) return; 
    try {
      setLimitsLoading(true);
      const requestObject = {
        body: {
          sdoMonth: sdoMonthYear.sdoMonth,
          sdoYear: sdoMonthYear.sdoYear,
          rank: position.rank,
          base: position.base,
          equipment: position.equipment
        }
      };
      const positionData = await API.post('crewlife', `/sdo/limits`, requestObject);
      setPositionAvailabilityData(positionData);
      setLimitsLoading(false);
    } catch (e: any) {
      setLimitsLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  }

  const checkCurrentMonthManualUpdateChanged = async(month: number, position: SdoPosition, year: number) => {
    if(!user) return;
    try {
      setLimitsLoading(true);
      const requestObject = {
        body: {
          sdoMonth: month,
          sdoYear: year,
          rank: position.rank,
          base: position.base,
          equipment: position.equipment
        }
      };
      const positionData = await API.post('crewlife', `/sdo/limits`, requestObject);
      setManualUpdate({
        timestamp: Date.now(),
        value: positionData.manualChange
      });
      setLimitsLoading(false);
    } catch (e: any) {
      setLimitsLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  }

  const checkCurrentMonthAvailabilityChanged = async(month: number, position: SdoPosition, year: number, currentMonthAvailability: number) => {
    if(!user) return; 
    try {
      setLimitsLoading(true);
      const requestObject = {
        body: {
          sdoMonth: month,
          sdoYear: year,
          rank: position.rank,
          base: position.base,
          equipment: position.equipment
        }
      };
      const positionData = await API.post('crewlife', `/sdo/limits`, requestObject);
      setCurrentMonthLimitChanged({
        timestamp: Date.now(),
        value: !(positionData.requestList[0].sdoAvailability === currentMonthAvailability)
      })
      setLimitsLoading(false);
    } catch (e: any) {
      setLimitsLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  }

  const getSdoInfoTabData = async() => {
    if(!user) return; 
    try {
      setLimitsLoading(true);
      const requestObject = {
        body: {
          "year": getEasternTime().getFullYear()
        }
      };
      const sdoSettings = await API.post('crewlife', `/sdo/settings/retrieve`, requestObject);
      setSdoSettings(sdoSettings[0])
      setLimitsLoading(false);
    } catch (e: any) {
      setLimitsLoading(false);
      if (e.response?.status) setErrorCode(e.response?.status);
      else setErrorCode(error.DEFAULT);
    }
  }

  return (
    <SdoAdminEligibilityCriteriaContext.Provider
      value={{
        loading,
        pilotRequestHistoryLoading,
        exceptionRevokeLoading,
        limitsLoading,
        exceptionRevokeResponse,
        pilotAutocomplete,
        pilotRequestsData,
        requestsData,
        sdoDates,
        sdoPositions,
        positionAvailabilityData,
        manualUpdate,
        currentMonthLimitChanged,
        sdoSettings,
        getManageRequestsData,
        getPilotAutocomplete,
        getPilotRequestHistory,
        getSdoDates,
        getSdoPositions,
        grantCancelException,
        revokeRequest,
        saveAvailabilities,
        viewPosition,
        checkCurrentMonthManualUpdateChanged,
        checkCurrentMonthAvailabilityChanged,
        getSdoInfoTabData
      }}>
      {props.children}
    </SdoAdminEligibilityCriteriaContext.Provider>
  );
};

export const useSdoAdminEligibilityCriteriaProvider = () => {
  return useContext(SdoAdminEligibilityCriteriaContext);
};

